import { TextField, Tooltip } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import React, { JSX } from 'react';

import { CategoryMultiSelect } from '~/src/dash/components/dropdowns/category-multiselect';
import { IModel } from '~/src/dash/components/model';
import { ModelSelectView } from '~/src/dash/components/model-select-view';
import { SingleSelect } from '~/src/dash/views/singleselect-view';

export interface IFormData {
  [key: string]: any;
}

export interface IFormItem {
  id: string;
  label: React.ReactNode | ((fi: IFormItem, data: IFormData) => React.ReactNode);
  render: (schema: IFormItemProps) => JSX.Element;

  name?: string;
  type?: string;
  className?: any;
  required?: boolean;
  disabled?: boolean;
  onUpdate?: () => IFormData;
  validate?: (fi: IFormItem, oldData: IFormData, newData: IFormData) => string | undefined;
  validateAsync?: (
    fi: IFormItem,
    oldData: IFormData,
    newData: IFormData,
    done: (errors: string | undefined) => void,
  ) => void;
  onKeyPress?: (key: any) => void;

  handleSnackbar?: (message: string, error: boolean, warning: boolean) => void;

  props?: {
    [key: string]: any;
  };
}

export interface IFormItemOverride {
  className?: any;
  disabled?: boolean;
}

function getSchemaName(schema: IFormItem): string {
  return schema.name || schema.id;
}

function updateFormData(schema: IFormItem, val: string | string[], data: IFormData): IFormData {
  if (schema.onUpdate) {
    return {
      ...data,
      ...schema.onUpdate(),
    };
  }
  return {
    ...data,
    [getSchemaName(schema)]: val,
  };
}

function getSchemaLabel(schema: IFormItem, data: IFormData) {
  if (typeof schema.label === 'function') {
    return schema.label(schema, data);
  }
  return schema.label;
}

function getSchemaValue(schema: IFormItem, data: IFormData) {
  return (getSchemaName(schema) in data && data[getSchemaName(schema)]) || '';
}

const textValidators = [
  (schema: IFormItem, _oldData: IFormData, newData: IFormData): string | undefined => {
    if (schema.required && !newData[getSchemaName(schema)]) {
      return 'Required!';
    }
    return undefined;
  },
];

function validateFormDataText(schema: IFormItem, oldData: IFormData, newData: IFormData): string[] {
  return textValidators
    .map((fn) => {
      return fn(schema, oldData, newData);
    })
    .filter((msg: string | undefined) => {
      return !!msg;
    }) as string[];
}

export interface IRenderFormProps {
  schema: IFormItem[];
  schemaOverrides?: { [id: string]: IFormItemOverride };
  data: IFormData;
  errors?: { [key: string]: string[] };
  onUpdate: (data: IFormData) => void;
  handleSnackbar?: (message: string, error: boolean, warning: boolean) => void;
}

interface IState {
  errors: { [key: string]: string[] };
}

export class RenderForm extends React.Component<IRenderFormProps, IState> {
  state = {
    errors: this.props.errors || ({} as { [key: string]: string[] }),
  };

  componentDidMount() {
    this.setState({
      errors: this.props.errors || ({} as { [key: string]: string[] }),
    });
  }

  componentWillReceiveProps(nextProps: IRenderFormProps) {
    this.setState({
      errors: nextProps.errors || ({} as { [key: string]: string[] }),
    });
  }

  onErrors = (id: string, errors: string[]) => {
    this.setState({
      errors: {
        ...this.state.errors,
        [id]: errors,
      },
    });
  };

  renderFormItem = (schema: IFormItem): JSX.Element => {
    return schema.render({
      schema,
      data: this.props.data,
      errors: this.state.errors[getSchemaName(schema)] || [],
      onUpdate: this.props.onUpdate,
      onErrors: (errs: string[]) => {
        this.onErrors(getSchemaName(schema), errs);
      },
      handleSnackbar: this.props.handleSnackbar,
    });
  };

  render() {
    const { schema, schemaOverrides } = this.props;
    return (
      <div>
        {(schema && schema.length >= 0 ? schema : []).map((sch: IFormItem) => {
          return (
            <div key={'item_' + getSchemaName(sch)}>
              {this.renderFormItem({
                ...sch,
                ...(schemaOverrides || {})[sch.id],
              })}
            </div>
          );
        })}
      </div>
    );
  }
}

interface IFormItemProps {
  schema: IFormItem;
  data: IFormData;
  errors: string[];

  onUpdate: (data: IFormData) => void;
  onErrors: (errors: string[]) => void;
  handleSnackbar?: (message: string, error: boolean, warning: boolean) => void;
}

function getAutocompleteNameFromType(type: string): string {
  if (type === 'password') {
    return 'current-password';
  }

  return '';
}

export function FormItemTypeText({ schema, data, errors, onErrors, onUpdate }: IFormItemProps) {
  return (
    <div>
      <TextField
        autoComplete={getAutocompleteNameFromType(schema.type || '')}
        onKeyPress={schema.onKeyPress}
        id={schema.id}
        label={getSchemaLabel(schema, data)}
        className={schema.className}
        value={getSchemaValue(schema, data)}
        type={schema.type ? schema.type : 'text'}
        disabled={schema.disabled}
        fullWidth
        margin='normal'
        error={errors.length > 0 && !!errors[0]}
        helperText={errors[0] || ''}
        onChange={({ currentTarget }: React.ChangeEvent<HTMLInputElement>) => {
          const newData = updateFormData(schema, currentTarget.value, data);
          const errs = validateFormDataText(schema, data, newData);
          if (schema.validate) {
            const er = schema.validate(schema, data, newData);
            if (er) {
              errs.push(er);
            }
          }
          onErrors(errs);
          onUpdate(newData);
        }}
        {...schema.props}
      />
    </div>
  );
}

export function FormItemTypePassword({ schema, data, errors, onErrors, onUpdate }: IFormItemProps) {
  return FormItemTypeText({
    schema: {
      ...schema,
      props: {
        ...(schema.props || {}),
        type: 'password',
      },
    },
    data,
    errors,
    onErrors,
    onUpdate,
  });
}

export function FormItemTypeSingleSelect({
  schema,
  data,
  errors,
  onErrors,
  onUpdate,
  handleSnackbar,
}: IFormItemProps) {
  // this prevents the component from loading without items
  if (!schema.props || schema.props.items.length === 0) {
    return <></>;
  }
  return (
    <div style={{ marginTop: '16px', marginBottom: '8px' }}>
      <SingleSelect
        disableAlphanumSort={schema.props?.disableAlphanumSort as boolean}
        label={getSchemaLabel(schema, data)}
        name={getSchemaName(schema)}
        onChange={(_name: string, value: string) => {
          if (
            _name === 'EnableIASFirstPartyVASTTracking' &&
            value === 'True' &&
            data.IASAnID === '' &&
            handleSnackbar
          ) {
            value = 'False';
            handleSnackbar(
              'Please talk to your account manager to enable first party measurement tools.',
              false,
              true,
            );
          }
          const newData = updateFormData(schema, value, data);
          const errs = validateFormDataText(schema, data, newData);
          if (schema.validate) {
            const er = schema.validate(schema, data, newData);
            if (er) {
              errs.push(er);
            }
          }
          onErrors(errs);
          onUpdate(newData);
        }}
        value={getSchemaValue(schema, data)}
        helperText={(errors.length > 0 && errors[0]) || ''}
        items={schema.props.items}
      />
      {getSchemaName(schema) === 'EnableIASFirstPartyVASTTracking' && (
        <Tooltip
          title={
            'This appends an IAS tag to your endpoint responses, measuring statistics such as rendered ads, viewable impressions, and invalid traffic.'
          }
        >
          <InfoIcon
            style={{
              verticalAlign: 'center',
              width: '1rem',
              color: '#dedede',
              marginLeft: '5px',
              position: 'relative',
              left: '120px',
              bottom: '53px',
            }}
          />
        </Tooltip>
      )}
    </div>
  );
}

export function FormItemTypeMultiSelect({
  schema,
  data,
  errors,
  onErrors,
  onUpdate,
}: IFormItemProps) {
  return (
    <div style={{ marginTop: '16px', marginBottom: '8px' }}>
      <CategoryMultiSelect
        label={getSchemaLabel(schema, data)}
        name={getSchemaName(schema)}
        onChange={(_name: string, value: string[]) => {
          const newData = updateFormData(schema, value, data);
          const errs = validateFormDataText(schema, data, newData);
          if (schema.validate) {
            const er = schema.validate(schema, data, newData);
            if (er) {
              errs.push(er);
            }
          }
          onErrors(errs);
          onUpdate(newData);
        }}
        values={getSchemaValue(schema, data)}
        search={false}
        selectAll={true}
        helperText={(errors.length > 0 && errors[0]) || ''}
        {...schema.props}
      />
    </div>
  );
}

export function FormItemTypeModelSelect({
  schema,
  data,
  errors,
  onErrors,
  onUpdate,
}: IFormItemProps) {
  return (
    <div style={{ marginTop: '16px', marginBottom: '8px' }}>
      <ModelSelectView
        label={getSchemaLabel(schema, data)}
        onSelect={(model: IModel) => {
          const newData = updateFormData(schema, model.ID, data);
          const errs = validateFormDataText(schema, data, newData);
          if (schema.validate) {
            const er = schema.validate(schema, data, newData);
            if (er) {
              errs.push(er);
            }
          }
          onErrors(errs);
          onUpdate(newData);
        }}
        helperText={(errors.length > 0 && errors[0]) || ''}
        {...schema.props}
      />
    </div>
  );
}
